import type { Doc } from "building-types/doc";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import { docEntityTypeToChildTypes } from "building-types/doc/docEntityTypeToForeignKeyRelations";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { RWTable, Table } from "common/data/table";
import { LRUObjectCache } from "common/utils/lruObjectCache";
import { docEntityHandleHash } from "./core/handleHash";
import docEntityGetParent from "./docEntityGetParent";
import { docEntityTypeToDocTableName } from "./docEntityTypeToDocTableName";
import { docForEachEntityHandleOfType } from "./docForEachEntity";
import { docGetGroupImmediateChildren } from "./docGetGroupImmediateChildren";

export function docEntityForEachChild(
  doc: Doc,
  parentHandle: DocEntityHandle,
  func: (handle: DocEntityHandle) => void,
  recursive: boolean = false,
  ignoreGroups: boolean = true,
  visited: RWTable<boolean> = {},
) {
  const parentHash = docEntityHandleHash(parentHandle);

  if (visited[parentHash]) {
    return;
  }

  visited[parentHash] = true;

  docEntityForEachChildFromParent(doc, parentHandle, func, recursive, ignoreGroups, visited);
}

function docEntityForEachChildFromParent(
  doc: Doc,
  parentHandle: DocEntityHandle,
  func: (handle: DocEntityHandle) => void,
  recursive: boolean,
  ignoreGroups: boolean,
  visited: RWTable<boolean>,
): void {
  const childHandleTypes = docEntityTypeToChildTypes[parentHandle.type];

  const visit = (child: DocEntityHandle) => {
    const hash = docEntityHandleHash(child);
    if (visited[hash]) {
      return;
    }

    visited[hash] = true;

    func(child);

    if (recursive) {
      docEntityForEachChildFromParent(doc, child, func, recursive, ignoreGroups, visited);
    }
  };

  if (parentHandle.type === "GROUP") {
    for (const child of docGetGroupImmediateChildren(doc, parentHandle)) {
      visit(child);
    }
    return;
  }

  if (ignoreGroups && childHandleTypes.length === 0) {
    return;
  }

  if (ignoreGroups) {
    for (const type of childHandleTypes) {
      docEntityForEachChildOfType(doc, type, parentHandle, visit);
    }
  } else {
    for (const type of childHandleTypes) {
      docEntityForEachChildOfType(doc, type, parentHandle, (child) => {
        if (!doc.entityGroups[docEntityHandleHash(child)]) {
          visit(child);
        }
      });
    }

    docEntityForEachChildOfType(doc, "GROUP", parentHandle, (child) => {
      if (!doc.entityGroups[docEntityHandleHash(child)]) {
        visit(child);
      }
    });
  }
}

function docBuildParentToChildTable(doc: Doc, type: DocEntityType): Table<DocEntityHandle[]> {
  const parentToChildren: RWTable<DocEntityHandle[]> = {};

  docForEachEntityHandleOfType(doc, type, (handle) => {
    const parent = docEntityGetParent(doc, handle);
    if (!parent) {
      return;
    }

    const hash = docEntityHandleHash(parent);

    if (!parentToChildren[hash]) {
      parentToChildren[hash] = [handle];
    } else {
      parentToChildren[hash].push(handle);
    }
  });

  return parentToChildren;
}

// Stores a map from type to lru cache where the lru cache stores a map from the doc to the parent-child table.
// This very significantly speeds up enumeration of children. The construction cost is a high coefficient
// O(n), but subsequent calls are close to constant time.
const typeToParentChildCache: {
  [type in DocEntityType]?: LRUObjectCache<Doc, Table<DocEntityHandle[]>>;
} = {};

function docGetParentToChildTable(doc: Doc, type: DocEntityType): Table<DocEntityHandle[]> {
  let parentChildCache = typeToParentChildCache[type];

  if (!parentChildCache) {
    const tableName = docEntityTypeToDocTableName[type];
    // Table-based entities live in exactly one table, so the table object's identity is a sufficient
    // cache key; other types fall back to the whole doc's identity (a de-opt, but still correct).
    parentChildCache = new LRUObjectCache<Doc, Table<DocEntityHandle[]>>(4, undefined, undefined, {
      // `doc[tableName]` is always present at runtime (an empty table when there are no entities);
      // the `?? doc` only satisfies the optional-table type.
      keyFn: (doc) => (tableName ? doc[tableName] ?? doc : doc),
    });

    typeToParentChildCache[type] = parentChildCache;
  }

  let currentParentChildTable = parentChildCache.get(doc);
  if (!currentParentChildTable) {
    currentParentChildTable = docBuildParentToChildTable(doc, type);
    parentChildCache.add(doc, currentParentChildTable);
  }

  return currentParentChildTable;
}

function docEntityForEachChildOfType(
  doc: Doc,
  type: DocEntityType,
  parent: DocEntityHandle,
  func: (handle: DocEntityHandle) => void,
): void {
  const children = docGetParentToChildTable(doc, type)[docEntityHandleHash(parent)];
  if (!children) {
    return;
  }

  for (let i = 0; i < children.length; i++) {
    func(children[i]);
  }
}

export function docEntityGetChildrenOfType(doc: Doc, type: DocEntityType, parent: DocEntityHandle): DocEntityHandle[] {
  const children = docGetParentToChildTable(doc, type)[docEntityHandleHash(parent)];
  if (!children) {
    return [];
  }

  // Make a copy to avoid mutation of the cached parent-child arrays.
  return [...children];
}
